/**
 * /accept-invite — public route.
 *
 * Reads ?token= from URL, calls GET /api/auth/invitations/{token} to
 * validate and fetch email/role, then shows a name + password form.
 * Submitting calls POST /api/auth/invitations/{token}/accept and
 * stores the returned JWT to log the new user straight in.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/auth/store";
import type { AuthToken, User } from "@/lib/types";

export const Route = createFileRoute("/accept-invite")({
  component: AcceptInvitePage,
});

interface InvitationOut {
  email: string;
  role: string;
  expires_at: string;
  purpose: "team_member" | "demo_reviewer";
  full_name: string | null;
  organization_name: string | null;
  job_title: string | null;
  demo_interest: string | null;
}

function AcceptInvitePage() {
  const navigate = useNavigate();
  const setAuth = useAuth((s) => s.setAuth);
  const search = new URLSearchParams(window.location.search);
  const token = search.get("token") ?? "";

  const [invitation, setInvitation] = useState<InvitationOut | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError("Missing invitation token.");
      return;
    }
    api<InvitationOut>(`/api/auth/invitations/${token}`)
      .then((data) => {
        setInvitation(data);
        if (data.full_name) setFullName(data.full_name);
      })
      .catch(() => setLoadError("This invitation is invalid or has expired."));
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!invitation) return;
    if (password !== confirm) {
      setSubmitError("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      setSubmitError("Password must be at least 8 characters");
      return;
    }
    if (!fullName.trim()) {
      setSubmitError("Full name is required");
      return;
    }
    setBusy(true);
    setSubmitError(null);
    try {
      const authToken = await api<AuthToken>(
        `/api/auth/invitations/${token}/accept`,
        {
          method: "POST",
          body: { full_name: fullName.trim(), password },
        },
      );
      setToken(authToken.access_token);
      const me = await api<User>("/api/auth/me");
      setAuth(authToken.access_token, me);
      // New users have no entity yet — send them through the provisioning wizard.
      // Existing users (re-invite edge case) skip straight to the app.
      const entities = await api<{ id: string }[]>("/api/auth/me/entities").catch(() => []);
      if (entities.length === 0 && invitation.purpose === "demo_reviewer") {
        navigate({
          to: "/onboarding/setup",
          search: {
            mode: "demo",
            profile: invitation.demo_interest ?? undefined,
          },
        });
      } else {
        navigate({ to: entities.length === 0 ? "/onboarding/setup" : "/contracts" });
      }
    } catch (exc: unknown) {
      const msg = exc instanceof Error ? exc.message : "Failed to accept invitation";
      setSubmitError(msg);
    } finally {
      setBusy(false);
    }
  }

  if (!token || loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-gray-800">
        <div className="w-full max-w-sm bg-white rounded-lg shadow p-8 dark:bg-gray-900">
          <p className="text-red-600">{loadError ?? "Missing invitation token."}</p>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-gray-800">
        <div className="w-full max-w-sm bg-white rounded-lg shadow p-8 dark:bg-gray-900">
          <p className="text-sm text-slate-500">Loading invitation…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-gray-800">
      <div className="w-full max-w-sm bg-white rounded-lg shadow p-8 dark:bg-gray-900">
        <h1 className="text-2xl font-semibold text-slate-900 mb-1 dark:text-slate-100">
          {invitation.purpose === "demo_reviewer"
            ? "Create your private demo account"
            : "Accept invitation"}
        </h1>
        {invitation.purpose === "demo_reviewer" ? (
          <p className="text-sm text-slate-500 mb-4 dark:text-slate-400">
            Your sample company uses dummy data and is completely separate from live books.
          </p>
        ) : (
          <p className="text-sm text-slate-500 mb-1 dark:text-slate-400">
            Joining as <strong>{invitation.role}</strong>
          </p>
        )}
        <p className="text-sm text-slate-500 mb-6 dark:text-slate-400">
          Account: <strong>{invitation.email}</strong>
        </p>
        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Full name
            </span>
            <input
              type="text"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              autoFocus
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Password
            </span>
            <input
              type="password"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Confirm password
            </span>
            <input
              type="password"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={8}
              required
            />
          </label>
          {submitError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {submitError}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? "Creating account…" : "Create account"}
          </button>
        </form>
      </div>
    </div>
  );
}
